import type {
  GetCmrCustomDataV2ResponseOutDTO,
  NullableBigDecimalWrapper,
  NullableStringWrapper,
  UpdateCmrCustomDataV2ManipulationUnitRequestInDTO,
} from '@api/ringil-api.v2.tsx';

/**
 * RD-3062 — the form ↔ API mapping of `CMREditForm`, moved out of the component unchanged so it can be
 * unit-tested. The component keeps the hooks, API calls and UI; everything here is pure.
 *
 * The CMR override API distinguishes "not overridden" (`null`) from "overridden to empty" (`{ value: null }`),
 * which is what the nullable wrappers below encode.
 */

export interface ManipulationUnitFormData {
  mpSignoNumber?: NullableStringWrapper;
  mpCount?: NullableBigDecimalWrapper;
  mpPackagingName?: NullableStringWrapper;
  mpName?: NullableStringWrapper;
  mpStatisticsNumber?: NullableStringWrapper;
  mpCompleteWeight?: NullableBigDecimalWrapper;
  mpVolume?: NullableBigDecimalWrapper;
  position?: number;
}

export interface UpdateCmrCustomFormData {
  completeSender?: NullableStringWrapper;
  completeConsignee?: NullableStringWrapper;
  successiveCarriers?: NullableStringWrapper;
  attachedDocumentsNumber5?: NullableStringWrapper;
  sendersInstructions?: NullableStringWrapper;
  toBePaidBy?: NullableStringWrapper;
  instructionAsToPaymentForCarriage?: NullableStringWrapper;
  specialAgreements?: NullableStringWrapper;
  estabilishedIn?: NullableStringWrapper;
  dzvvNumber?: NullableStringWrapper;
  rideNumber?: NullableStringWrapper;
  borderCrossings?: NullableStringWrapper;
  others?: NullableStringWrapper;
  attachedDocumentsNumber30?: NullableStringWrapper;
  confirmationOfCustomsPapers?: NullableStringWrapper;
  cashOnDelivery?: NullableStringWrapper;
  carriersReservationsAndObservations?: NullableStringWrapper;
  weight?: NullableBigDecimalWrapper;
  unClass?: NullableStringWrapper;
  manipulationUnits?: ManipulationUnitFormData[];
}

/** A typed text field: no override while both it and the stored override are empty, else the (possibly cleared) value. */
export const createNullableString = (value?: string, originalValue?: string): NullableStringWrapper | null => {
  const isNewEmpty = value === undefined || value === null || value === '';
  const isOriginalEmpty = originalValue === undefined || originalValue === null;
  if (isOriginalEmpty && isNewEmpty) return null;
  if (isNewEmpty) return { value: null };
  return { value };
};

/** A typed number field, with the same override rules as `createNullableString`. */
export const createNullableNumber = (value?: number, originalValue?: number): NullableBigDecimalWrapper | null => {
  const isNewEmpty = value === undefined || value === null;
  const isOriginalEmpty = originalValue === undefined || originalValue === null;
  if (isOriginalEmpty && isNewEmpty) return null;
  if (isNewEmpty) return { value: null };
  return { value };
};

/** A manipulation unit with nothing filled in. */
export const isUnitEmpty = (unit: UpdateCmrCustomDataV2ManipulationUnitRequestInDTO) =>
  !unit.mpSignoNumber?.value &&
  !unit.mpCount?.value &&
  !unit.mpPackagingName?.value &&
  !unit.mpName?.value &&
  !unit.mpStatisticsNumber?.value &&
  !unit.mpCompleteWeight?.value &&
  !unit.mpVolume?.value;

/** The body of `updateCustomDataForCMRV2`: the form, with empty manipulation units left out. */
export const buildCmrUpdateRequest = (values: UpdateCmrCustomFormData) => {
  const filteredValues = {
    ...values,
    manipulationUnits: Array.isArray(values.manipulationUnits)
      ? values.manipulationUnits.length > 0
        ? values.manipulationUnits.filter(mu => !isUnitEmpty(mu))
        : null
      : null,
  };
  return filteredValues;
};

/** The form for the stored CMR overrides; one empty manipulation unit when none is stored. */
export const cmrCustomDataToFormValues = (data: GetCmrCustomDataV2ResponseOutDTO): UpdateCmrCustomFormData => ({
  completeSender: data?.completeSender ? { value: data.completeSender } : null,
  completeConsignee: data?.completeConsignee ? { value: data.completeConsignee } : null,
  successiveCarriers: data?.successiveCarriers ? { value: data.successiveCarriers } : null,
  carriersReservationsAndObservations: data?.carriersReservationsAndObservations ? { value: data.carriersReservationsAndObservations } : null,
  attachedDocumentsNumber5: data?.attachedDocumentsNumber5 ? { value: data.attachedDocumentsNumber5 } : null,
  sendersInstructions: data?.sendersInstructions ? { value: data.sendersInstructions } : null,
  toBePaidBy: data?.toBePaidBy ? { value: data.toBePaidBy } : null,
  cashOnDelivery: data?.cashOnDelivery ? { value: data.cashOnDelivery } : null,
  instructionAsToPaymentForCarriage: data?.instructionAsToPaymentForCarriage ? { value: data.instructionAsToPaymentForCarriage } : null,
  estabilishedIn: data?.estabilishedIn ? { value: data.estabilishedIn } : null,
  specialAgreements: data?.specialAgreements ? { value: data.specialAgreements } : null,
  dzvvNumber: data?.dzvvNumber ? { value: data.dzvvNumber } : null,
  borderCrossings: data?.borderCrossings ? { value: data.borderCrossings } : null,
  others: data?.others ? { value: data.others } : null,
  weight: data?.weight ? { value: data.weight } : null,
  rideNumber: data?.rideNumber ? { value: data.rideNumber } : null,
  attachedDocumentsNumber30: data?.attachedDocumentsNumber30 ? { value: data.attachedDocumentsNumber30 } : null,
  confirmationOfCustomsPapers: data?.confirmationOfCustomsPapers ? { value: data.confirmationOfCustomsPapers } : null,
  unClass: data?.unClass ? { value: data.unClass } : null,
  manipulationUnits: data?.manipulationUnits?.length ? data.manipulationUnits.map(mu => {
    return {
      mpSignoNumber: mu.mpSignoNumber ? { value: mu.mpSignoNumber } : null,
      mpCount: mu.mpCount ? { value: mu.mpCount } : null,
      mpPackagingName: mu.mpPackagingName ? { value: mu.mpPackagingName } : null,
      mpName: mu.mpName ? { value: mu.mpName } : null,
      mpStatisticsNumber: mu.mpStatisticsNumber ? { value: mu.mpStatisticsNumber } : null,
      mpCompleteWeight: mu.mpCompleteWeight ? { value: mu.mpCompleteWeight } : null,
      mpVolume: mu.mpVolume ? { value: mu.mpVolume } : null,
      position: mu.position,
    };
  }) : [{
    mpSignoNumber: null,
    mpCount: null,
    mpPackagingName: null,
    mpName: null,
    mpStatisticsNumber: null,
    mpCompleteWeight: null,
    mpVolume: null,
    position: 0,
  }],
});

/** A blank manipulation unit, positioned after the units the form already holds. */
export const newManipulationUnit = (values: UpdateCmrCustomFormData) => {
  const newUnit = {
    mpSignoNumber: null,
    mpCount: null,
    mpPackagingName: null,
    mpName: null,
    mpStatisticsNumber: null,
    mpCompleteWeight: null,
    mpVolume: null,
    position: values.manipulationUnits?.length ?? 0,
  } as UpdateCmrCustomDataV2ManipulationUnitRequestInDTO;
  return newUnit;
};
